import React, { useState, useEffect } from 'react'
import classNames from 'classnames'
import { PhoneField, PhoneFieldProps } from '@deal/components'
import { ParsedPhoneNumber } from '#src/generated/graphql'
import useClickOutside from '#src/hooks/useClickOutside'
import DownArrow from './down-arrow.svg'
import countries from './countries'
import styles from './styles.css'
import flagSprite from './flags.png'
import flagStyles from './flags.css'
import BackgroundImage from '../BackgroundImage'

interface Country {
  name: string
  dialCode: string
  code: string
}

interface InternationalPhoneFieldProps
  extends Omit<PhoneFieldProps, 'onChange' | 'label' | 'labelHidden' | 'errorText'> {
  prioritizedCountryCodes?: string[]
  iconSize?: number
  defaultCountryCode?: string
  label: string
  labelHidden?: boolean
  errorText?: string
  onChange: (phoneNumber: string) => void
  parsedPhoneNumber?: ParsedPhoneNumber | null
}

const InternationalPhoneField: React.SFC<InternationalPhoneFieldProps> = ({
  prioritizedCountryCodes = ['US', 'CA', 'GB'],
  defaultCountryCode = 'US',
  onChange,
  errorText,
  label,
  labelHidden,
  parsedPhoneNumber,
  ...rest
}) => {
  /**
   * Check to see if there is a parsed phone number passed in and try to match the country code (US, CA, etc.)
   */
  const existingCountry = countries.find(country => {
    if (parsedPhoneNumber && parsedPhoneNumber.regionCode === country.code) {
      return country
    }
  })

  /**
   * The default country is what comes off of the maybe parsed phone number, otherwise the default prop
   */
  const defaultCountry =
    existingCountry || countries.find(country => country.code === defaultCountryCode)
  if (!defaultCountry) {
    throw new Error(
      `Invalid default country code or country not found in list: ${defaultCountryCode}`
    )
  }

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [currentPhoneValue, setCurrentPhoneValue] = useState<string | undefined>(
    (parsedPhoneNumber && parsedPhoneNumber.nationalNumber) || undefined
  )
  const [currentCountry, setCurrentCountry] = useState<Country>(defaultCountry)

  /**
   * Prioritize some countries to the top of the list, filter out any mismatches
   */
  const filteredPrioritzedCoutryCodes = prioritizedCountryCodes.filter(prioritizedCountryCode =>
    countries.find(country => country.code === prioritizedCountryCode)
  )
  const sortedCountries = [
    ...filteredPrioritzedCoutryCodes.map(filteredPrioritizedCountryCode =>
      countries.find(country => country.code === filteredPrioritizedCountryCode)
    ),
    ...countries
  ]

  const menuRef = useClickOutside<HTMLDivElement>(() => {
    setIsMenuOpen(false)
  })

  useEffect(() => {
    /**
     * Only trigger the onChange event if a country and phone are present,
     * otherwise invalidate the field and reset it.
     */
    if (currentCountry && currentPhoneValue) {
      onChange(`${currentCountry.dialCode}${currentPhoneValue}`)
    } else if (currentPhoneValue !== undefined) {
      onChange('')
    }
  }, [currentCountry, currentPhoneValue])

  const currentCountryFlag = flagStyles[`flag-${currentCountry.code.toLowerCase()}`]

  return (
    <div className={styles.container}>
      {!labelHidden && <div className={styles.label}>{label}</div>}
      <div className={styles.field}>
        <div className={styles.flagDropdown} onClick={() => setIsMenuOpen(!isMenuOpen)}>
          <div className={styles.selectedCountry}>
            {currentCountry && (
              <>
                <BackgroundImage
                  src={`${flagSprite}?auto=compress`}
                  containerClassName={flagStyles.flagContainer}
                  imageClassName={classNames(flagStyles.flag, currentCountryFlag)}
                  width={512}
                  height={512}
                />
                <span className={styles.dialCode}>{currentCountry.dialCode}</span>
                <DownArrow
                  width={12}
                  className={classNames(styles.downArrow, {
                    [styles.inverseArrow]: isMenuOpen
                  })}
                />
              </>
            )}
          </div>
          <div
            className={classNames(styles.selectList, {
              [styles.selectListExpanded]: isMenuOpen
            })}
            ref={menuRef}
          >
            {sortedCountries.map(
              (country, idx) =>
                country && (
                  <div
                    key={idx}
                    onClick={() => {
                      if (currentCountry && country.code !== currentCountry.code) {
                        setCurrentCountry(country)
                      }

                      setIsMenuOpen(false)
                    }}
                    className={classNames(styles.selectListRow, {
                      [styles.bottomBorder]: idx === filteredPrioritzedCoutryCodes.length - 1
                    })}
                  >
                    {country.name}
                  </div>
                )
            )}
          </div>
        </div>

        <PhoneField
          label=""
          labelHidden={true}
          onChange={event => {
            const phoneValue = event.target.value
            setCurrentPhoneValue(phoneValue)
          }}
          onFocus={() => {
            setIsMenuOpen(false)
          }}
          defaultValue={currentPhoneValue}
          {...rest}
        />
      </div>
      {errorText && <div className={styles.errorText}>{errorText}</div>}
    </div>
  )
}

export default InternationalPhoneField
